import React, {useMemo} from "react";
import {
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography,
    useTheme,
} from "@mui/material";
import {Division} from "../../data/Division";
import {themedScrollbar} from "./themedScrollbar";

interface ResourceDivisionSidebarProps {
    divisions: Division[];
    selectedIdentifier: string | null;
    matchedDivisions?: Set<string>;
    searchQuery: string;
    onSelectDivision: (identifier: string | null) => void;
}

export function ResourceDivisionSidebar({
    divisions,
    selectedIdentifier,
    matchedDivisions,
    searchQuery,
    onSelectDivision,
}: ResourceDivisionSidebarProps) {
    const theme = useTheme();

    const isSearching = searchQuery.trim().length > 0;

    // Sort divisions: matched ones first when searching
    const sortedDivisions = useMemo(() => {
        if (!isSearching || !matchedDivisions) return divisions;
        return [...divisions].sort((a, b) => {
            const aMatch = matchedDivisions.has(a.identifier) ? 1 : 0;
            const bMatch = matchedDivisions.has(b.identifier) ? 1 : 0;
            return bMatch - aMatch;
        });
    }, [divisions, matchedDivisions, isSearching]);

    const handleClick = (identifier: string) => {
        if (selectedIdentifier === identifier) {
            onSelectDivision(null);
        } else {
            onSelectDivision(identifier);
        }
    };

    return (
        <Paper
            variant="outlined"
            sx={{
                width: {xs: "100%", md: 280},
                minWidth: {md: 280},
                flexShrink: 0,
                overflow: "hidden",
                alignSelf: "flex-start",
                position: {md: "sticky"},
                top: {md: 16},
            }}
        >
            <Typography
                variant="overline"
                sx={{px: 2, pt: 1.5, pb: 0.5, display: "block", color: theme.palette.text.secondary}}
            >
                Divisions
            </Typography>
            <List
                dense
                disablePadding
                sx={{
                    maxHeight: {md: "calc(100vh - 280px)"},
                    overflowY: "auto",
                    ...themedScrollbar(theme),
                }}
            >
                {sortedDivisions.map((division) => {
                    const isSelected = division.identifier === selectedIdentifier;
                    const isGrayedOut = isSearching && matchedDivisions !== undefined
                        && !matchedDivisions.has(division.identifier);

                    return (
                        <ListItemButton
                            key={division.identifier}
                            selected={isSelected}
                            onClick={() => handleClick(division.identifier)}
                            sx={{
                                py: 1.2,
                                px: 2,
                                alignItems: "center",
                                borderLeft: isSelected
                                    ? `3px solid ${theme.palette.primary.main}`
                                    : "3px solid transparent",
                                opacity: isGrayedOut ? 0.4 : 1,
                                transition: "opacity 0.2s",
                            }}
                        >
                            <ListItemIcon sx={{minWidth: 40, alignSelf: "center"}}>
                                <Box
                                    component="img"
                                    src={division.badge}
                                    loading="lazy"
                                    decoding="async"
                                    width={28}
                                    height={28}
                                    sx={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: "50%",
                                        filter: isGrayedOut ? "grayscale(1)" : "none",
                                        transition: "filter 0.2s",
                                    }}
                                />
                            </ListItemIcon>
                            <ListItemText
                                primary={
                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{fontWeight: isSelected ? 600 : 400}}
                                    >
                                        {division.displayName}
                                    </Typography>
                                }
                            />
                        </ListItemButton>
                    );
                })}
            </List>
        </Paper>
    );
}
